import React, { useEffect, useRef, useState } from 'react';
import { BodyText, FlexColumn, FlexRow } from '@make-software/cspr-design';
import { PluginFormControlsProps } from 'app/components/management/common/plugins/form-control-map';
import { ErrorText, ErrorTextContainer, StyledInput } from 'app/components/common/common-styles';

import { QuizPluginMetadataWire, QuizQuestionUI, QuizUIState } from './types';
import { metadataToUI, uiToMetadata } from './metadata-conventor';
import {
  QuizQuestionControl,
  StyledAddQuestionButton,
  StyledEmptyQuestionsBlock
} from './quiz-question-control';

export const QuizFormControls = ({ metadata, errors, onChange }: PluginFormControlsProps) => {
  // Initialize local UI state from metadata once (per mount)
  const [uiState, setUiState] = useState<QuizUIState>(() =>
    metadataToUI(metadata as QuizPluginMetadataWire | undefined)
  );

  // Keep latest onChange in a ref to avoid it being a dependency
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Whenever uiState changes, notify parent
  // Include `questions` alongside the wire format so yup can validate answer options
  useEffect(() => {
    onChangeRef.current({ ...uiToMetadata(uiState), questions: uiState.questions });
  }, [uiState]);

  const update = (patch: Partial<QuizUIState>) => {
    setUiState((prev) => ({ ...prev, ...patch }));
  };

  const addQuestion = () => {
    setUiState((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          key: '',
          title: '',
          answers: [],
          correctAnswer: ''
        }
      ]
    }));
  };

  const updateQuestion = (index: number, nextQuestion: QuizQuestionUI) => {
    setUiState((prev) => {
      const questions = [...prev.questions];
      questions[index] = nextQuestion;
      return { ...prev, questions };
    });
  };

  const removeQuestion = (index: number) => {
    setUiState((prev) => {
      const questions = prev.questions.filter((_, i) => i !== index);
      return { ...prev, questions };
    });
  };

  const metadataErrors = errors?.pluginMetadata || {};

  return (
    <FlexColumn gap={12} style={{ width: '100%' }}>
      {/* Basic quiz settings */}
      <StyledInput
        value={uiState.targetURL}
        label={<BodyText size={1}>Target URL with article / content</BodyText>}
        placeholder="https://read-this-article.com"
        onChange={(e) => update({ targetURL: e.target.value })}
      />
      {metadataErrors?.targetURL && (
        <ErrorTextContainer justify={'flex-start'}>
          <ErrorText size={3}>{metadataErrors.targetURL.message}</ErrorText>
        </ErrorTextContainer>
      )}

      {/* Questions block */}
      <FlexColumn style={{ width: '100%', marginTop: 16 }}>
        <BodyText size={1} scale="sm">
          Quiz questions
        </BodyText>

        {uiState.questions.length ? (
          uiState.questions.map((q, idx) => (
            <QuizQuestionControl
              key={idx}
              index={idx}
              question={q}
              onChange={(next) => updateQuestion(idx, next)}
              onRemove={() => removeQuestion(idx)}
              errors={
                Array.isArray(metadataErrors?.questions) ? metadataErrors.questions[idx] : null
              }
            />
          ))
        ) : (
          <StyledEmptyQuestionsBlock>No questions yet</StyledEmptyQuestionsBlock>
        )}

        <FlexRow>
          <StyledAddQuestionButton size={3} scale={'sm'} variation={'blue'} onClick={addQuestion}>
            + Add question
          </StyledAddQuestionButton>
        </FlexRow>

        {metadataErrors?.formSettings?.pages && metadataErrors?.formSettings.pages.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{metadataErrors?.formSettings.pages.message}</ErrorText>
          </ErrorTextContainer>
        )}
      </FlexColumn>
    </FlexColumn>
  );
};
